import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  showLead,
  updateLead,
  deleteLead,
  listLeadActivities,
  createLeadActivity,
  deleteLeadActivity,
  completeLeadActivity,
} from "../../api/endpoints/enrollmentCrm";

const formatDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    return d.toLocaleDateString();
  } catch {
    return "";
  }
};

const statusToChip = (status) => {
  switch (status) {
    case "pending":
      return <Chip size="small" label="Pendente" color="warning" variant="outlined" />;
    case "completed":
      return <Chip size="small" label="Concluída" color="success" variant="outlined" />;
    case "cancelled":
      return <Chip size="small" label="Cancelada" color="error" variant="outlined" />;
    default:
      return <Chip size="small" label={status} variant="outlined" />;
  }
};

const LeadDetailsDialog = ({
  open,
  leadId,
  initialTabIndex = 0,
  onClose,
  stages = [],
  segments = [],
  sources = [],
  currentUser = null,
  isAdmin = false,
  onDeleted,
  onSaved,
}) => {
  const [tab, setTab] = useState(0);
  const [loadingLead, setLoadingLead] = useState(false);
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  const activityInitial = useMemo(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const [activityForm, setActivityForm] = useState({
    type: "note",
    title: "",
    description: "",
    scheduledDate: activityInitial,
    status: "pending",
  });

  useEffect(() => {
    if (!open) return;
    setTab(initialTabIndex);
    setLead(null);
    setActivities([]);
  }, [open, leadId, initialTabIndex]);

  const fetchActivities = async (id) => {
    setLoadingActivities(true);
    try {
      const acts = await listLeadActivities(id);
      setActivities(acts || []);
    } finally {
      setLoadingActivities(false);
    }
  };

  useEffect(() => {
    if (!open || !leadId) return;
    const run = async () => {
      setLoadingLead(true);
      try {
        const details = await showLead(leadId);
        setLead(details);
        await fetchActivities(leadId);
      } finally {
        setLoadingLead(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId]);

  const updateField = (key, value) => {
    setLead((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveLead = async () => {
    if (!lead?.id) return;

    const studentAgeNum =
      lead.studentAge === "" || lead.studentAge === undefined || lead.studentAge === null
        ? null
        : Number(lead.studentAge);

    await updateLead(lead.id, {
      name: lead.name,
      phone: lead.phone,
      stageId: lead.stageId ?? null,
      segmentId: lead.segmentId ?? null,
      sourceId: lead.sourceId ?? null,
      studentName: lead.studentName ?? "",
      studentAge: Number.isNaN(studentAgeNum) ? null : studentAgeNum,
      schoolYear: lead.schoolYear ?? null,
      grade: lead.grade ?? null,
      shift: lead.shift ?? null,
      previousSchool: lead.previousSchool ?? null,
      motherName: lead.motherName ?? null,
      motherPhone: lead.motherPhone ?? null,
      fatherName: lead.fatherName ?? null,
      fatherPhone: lead.fatherPhone ?? null,
      referredBy: lead.referredBy ?? null,
      interestDetails: lead.interestDetails ?? null,
    });

    if (onSaved) await onSaved();
    const refreshed = await showLead(lead.id);
    setLead(refreshed);
  };

  const handleDeleteLead = async () => {
    if (!lead?.id) return;
    if (!window.confirm("Excluir este lead?")) return;
    await deleteLead(lead.id);
    if (onDeleted) await onDeleted(lead.id);
    onClose();
  };

  const handleCompleteActivity = async (activityId) => {
    await completeLeadActivity(activityId, { completedDate: new Date().toISOString() });
    await fetchActivities(leadId);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm("Excluir esta atividade?")) return;
    await deleteLeadActivity(activityId);
    await fetchActivities(leadId);
  };

  const handleAddActivity = async () => {
    if (!activityForm.title.trim()) {
      alert("Título da atividade é obrigatório.");
      return;
    }
    await createLeadActivity(leadId, {
      type: activityForm.type,
      title: activityForm.title.trim(),
      description: activityForm.description?.trim() || null,
      scheduledDate: activityForm.scheduledDate || null,
      status: activityForm.status,
    });
    setActivityForm((p) => ({ ...p, title: "", description: "" }));
    await fetchActivities(leadId);
  };

  const stageOptions = useMemo(() => (stages || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [stages]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {loadingLead ? "Carregando..." : lead?.name || "Lead"}
          </Typography>
          {isAdmin ? (
            <IconButton onClick={handleDeleteLead} size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loadingLead ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : lead ? (
          <Box>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth">
              <Tab label="Detalhes" />
              <Tab label="Atividades" />
            </Tabs>

            {tab === 0 ? (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nome do Responsável"
                      value={lead.name || ""}
                      onChange={(e) => updateField("name", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Telefone"
                      value={lead.phone || ""}
                      onChange={(e) => updateField("phone", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Nome do Aluno"
                      value={lead.studentName || ""}
                      onChange={(e) => updateField("studentName", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Idade"
                      value={lead.studentAge ?? ""}
                      onChange={(e) => updateField("studentAge", e.target.value)}
                      fullWidth
                      margin="dense"
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Ano Escolar"
                      value={lead.schoolYear || ""}
                      onChange={(e) => updateField("schoolYear", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Série"
                      value={lead.grade || ""}
                      onChange={(e) => updateField("grade", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Turno"
                      value={lead.shift || ""}
                      onChange={(e) => updateField("shift", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Escola anterior"
                      value={lead.previousSchool || ""}
                      onChange={(e) => updateField("previousSchool", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Detalhes de interesse"
                      value={lead.interestDetails || ""}
                      onChange={(e) => updateField("interestDetails", e.target.value)}
                      fullWidth
                      margin="dense"
                      multiline
                      minRows={2}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Mãe"
                      value={lead.motherName || ""}
                      onChange={(e) => updateField("motherName", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Telefone mãe"
                      value={lead.motherPhone || ""}
                      onChange={(e) => updateField("motherPhone", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Pai"
                      value={lead.fatherName || ""}
                      onChange={(e) => updateField("fatherName", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Telefone pai"
                      value={lead.fatherPhone || ""}
                      onChange={(e) => updateField("fatherPhone", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Indicado por"
                      value={lead.referredBy || ""}
                      onChange={(e) => updateField("referredBy", e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      label="Estágio"
                      value={lead.stageId ?? ""}
                      onChange={(e) => updateField("stageId", e.target.value ? Number(e.target.value) : null)}
                      fullWidth
                      margin="dense"
                    >
                      {stageOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      label="Segmento"
                      value={lead.segmentId ?? ""}
                      onChange={(e) => updateField("segmentId", e.target.value ? Number(e.target.value) : null)}
                      fullWidth
                      margin="dense"
                    >
                      <MenuItem value="">
                        <em>Selecione</em>
                      </MenuItem>
                      {(segments || []).map((seg) => (
                        <MenuItem key={seg.id} value={seg.id}>
                          {seg.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      label="Fonte"
                      value={lead.sourceId ?? ""}
                      onChange={(e) => updateField("sourceId", e.target.value ? Number(e.target.value) : null)}
                      fullWidth
                      margin="dense"
                    >
                      <MenuItem value="">
                        <em>Selecione</em>
                      </MenuItem>
                      {(sources || []).map((src) => (
                        <MenuItem key={src.id} value={src.id}>
                          {src.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button variant="contained" onClick={handleSaveLead}>
                    Salvar alterações
                  </Button>
                </Box>
              </Box>
            ) : null}

            {tab === 1 ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Atividades
                </Typography>

                {loadingActivities ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                  </Box>
                ) : activities.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma atividade registrada.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {activities.map((a) => (
                      <Paper key={a.id} variant="outlined" sx={{ p: 1.5 }}>
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              {statusToChip(a.status)}
                              <Typography sx={{ fontWeight: 800 }}>{a.title}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Data: {formatDate(a.scheduledDate)}
                            </Typography>
                            {a.description ? (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {a.description}
                              </Typography>
                            ) : null}
                          </Box>

                          <Box>
                            {a.status !== "completed" ? (
                              <IconButton size="small" onClick={() => handleCompleteActivity(a.id)}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                            {isAdmin ? (
                              <IconButton size="small" color="error" onClick={() => handleDeleteActivity(a.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Adicionar atividade
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Tipo"
                      value={activityForm.type}
                      onChange={(e) => setActivityForm((p) => ({ ...p, type: e.target.value }))}
                      fullWidth
                      margin="dense"
                    >
                      <MenuItem value="call">call</MenuItem>
                      <MenuItem value="email">email</MenuItem>
                      <MenuItem value="meeting">meeting</MenuItem>
                      <MenuItem value="note">note</MenuItem>
                      <MenuItem value="visit">visit</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Título"
                      value={activityForm.title}
                      onChange={(e) => setActivityForm((p) => ({ ...p, title: e.target.value }))}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Descrição"
                      value={activityForm.description}
                      onChange={(e) => setActivityForm((p) => ({ ...p, description: e.target.value }))}
                      fullWidth
                      margin="dense"
                      multiline
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Data"
                      type="date"
                      value={activityForm.scheduledDate}
                      onChange={(e) => setActivityForm((p) => ({ ...p, scheduledDate: e.target.value }))}
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Status"
                      value={activityForm.status}
                      onChange={(e) => setActivityForm((p) => ({ ...p, status: e.target.value }))}
                      fullWidth
                      margin="dense"
                    >
                      <MenuItem value="pending">pending</MenuItem>
                      <MenuItem value="completed">completed</MenuItem>
                      <MenuItem value="cancelled">cancelled</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: "flex", alignItems: "center" }}>
                    <Button variant="contained" onClick={handleAddActivity} disabled={!activityForm.title.trim()}>
                      Salvar atividade
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Selecione um lead.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadDetailsDialog;

